import { SearchPayload, SearchResponse } from "lib/api";
import { ApiPromiseClient } from "../apiClient/ApiClient";
import * as constants from "lib/api/constants";
import { MccUser } from "auth";
import { MapBusinessUserToMcc } from "models/mapper/user";
import {
	AddOrUpdateUserRequest,
	AddOrUpdateUserResponse,
	BusinessObjectDto,
	UserFilter,
	SearchGmidApiResponse,
	GmidUser,
	UserRoleNote,
	SectorAdminContactInfo,
	PlantUserAccessManagerContactInfo,
	ValidateUserExist,
	CreatePlantUserRequest,
	CreatePlantUserResponse,
	PlantUserResponse,
	UpdatePlantUserRequest,
	DeleteSectorAdminRequest,
	AddSectorAdminRequest,
	DeletePlantUserRequest,
} from "models/dto/user";
import { RoleSummary } from "models/user";

export async function SearchUsers(
	api: ApiPromiseClient,
	payload: SearchPayload<any, UserFilter>
): Promise<SearchResponse<any, UserFilter>> {
	// Return raw results so downstream mappers can handle evolving shapes (userData + userRoles)
	const resp = await api.Post<SearchResponse<any, UserFilter>>(
		constants.Paths().usersSearch,
		payload
	);
	return resp;
}

export async function AddUser(
	api: ApiPromiseClient,
	request: AddOrUpdateUserRequest
): Promise<{ response: AddOrUpdateUserResponse; businessUser: MccUser }> {
	const resp = await api.Post<any>(constants.Paths().updateUser, request);

	if (
		resp?.errorList &&
		Array.isArray(resp.errorList) &&
		resp.errorList.length
	) {
		const err: any = new Error(
			resp.errorList[0]?.message || "Update failed"
		);
		err.response = { data: resp };
		throw err;
	}

	const typedResp: AddOrUpdateUserResponse = resp as AddOrUpdateUserResponse;
	return {
		response: typedResp,
		businessUser: MapBusinessUserToMcc(typedResp.businessUser),
	};
}

export async function UpdateUser(
	api: ApiPromiseClient,
	request: AddOrUpdateUserRequest
): Promise<{ response: AddOrUpdateUserResponse; businessUser: MccUser }> {
	const resp = await api.Put<any>(constants.Paths().updateUser, request);

	if (
		resp?.errorList &&
		Array.isArray(resp.errorList) &&
		resp.errorList.length
	) {
		const err: any = new Error(
			resp.errorList[0]?.message || "Update failed"
		);
		err.response = { data: resp };
		throw err;
	}

	const typedResp: AddOrUpdateUserResponse = resp as AddOrUpdateUserResponse;
	return {
		response: typedResp,
		businessUser: MapBusinessUserToMcc(typedResp.businessUser),
	};
}

export async function Roles(api: ApiPromiseClient): Promise<RoleSummary[]> {
	const raw = await api.Get<any>(constants.Paths().roles);
	if (!Array.isArray(raw)) return [];
	return raw
		.filter((r) => r && r.id && r.name)
		.map((r) => ({ id: r.id, name: r.name })) as RoleSummary[];
}

/** Fetches all business objects from the security service. Used so Manage User can assign any permission type to others. */
export async function GetBusinessObjects(
	api: ApiPromiseClient
): Promise<BusinessObjectDto[]> {
	const path = constants.Paths().businessObjects;
	const raw = await api.Get<any>(path);
	if (!Array.isArray(raw)) return [];
	return raw
		.filter((r) => r && (r.id != null || r.objectId != null))
		.map((r) => ({
			id: String(r.id ?? r.objectId ?? ""),
			name: String(r.name ?? r.objectName ?? r.id ?? r.objectId ?? ""),
		}))
		.filter((bo) => bo.id.length > 0);
}

export async function DeleteUser(
	api: ApiPromiseClient,
	userRoleId: string
): Promise<void> {
	const base = constants.Paths().deleteUser;
	await api.Delete<void>(`${base}/${encodeURIComponent(userRoleId)}`);
}

export async function SearchGmid(
	api: ApiPromiseClient,
	id: string
): Promise<GmidUser[]> {
	const base = constants.Paths().searchGmid;
	const resp = await api.Get<SearchGmidApiResponse>(
		`${base}/${encodeURIComponent(id)}`
	);
	if (!resp || !Array.isArray(resp.resultList)) return [];
	return resp.resultList.filter(
		(r) => !!r && !!r.employeeId && !!r.displayName
	);
}

export async function GetUserRoleNotesByBusinessUserId(
	api: ApiPromiseClient,
	businessUserId: string
): Promise<UserRoleNote[]> {
	const path = constants.Paths().userRoleNotes(businessUserId);
	const resp = await api.Get<UserRoleNote[]>(path);
	if (!resp || !Array.isArray(resp)) return [];
	return resp;
}

/**
 * Sector admin contacts by sector. Drops malformed array elements so the UI never maps invalid rows.
 */
export async function FindSectorAdminContactInfo(
	api: ApiPromiseClient
): Promise<SectorAdminContactInfo[]> {
	const path = constants.Paths().sectorAdminContactInfo();
	const resp = await api.Get<SectorAdminContactInfo[]>(path);
	if (!resp || !Array.isArray(resp)) return [];
	return resp.filter(
		(r) => !!r && !!r.sectorId && !!r.sectorName && Array.isArray(r.admins)
	);
}

/**
 * Plant user access manager contacts by sector (Manage User grant, valid sector–plant on server).
 * Drops malformed array elements so the UI never maps invalid rows.
 */
export async function FindPlantUserAccessManagersContactInfo(
	api: ApiPromiseClient
): Promise<PlantUserAccessManagerContactInfo[]> {
	const path = constants.Paths().plantUserAccessManagersContactInfo();
	const resp = await api.Get<PlantUserAccessManagerContactInfo[]>(path);
	if (!resp || !Array.isArray(resp)) return [];
	return resp.filter(
		(r) =>
			!!r && !!r.sectorId && !!r.sectorName && Array.isArray(r.managers)
	);
}

export async function UpdateUserRoleNote(
	api: ApiPromiseClient,
	userRoleId: string,
	noteId: string
): Promise<void> {
	const path = constants.Paths().updateUserRoleNote(userRoleId, noteId);
	await api.Put(path);
}

export async function CheckUserExists(
	api: ApiPromiseClient,
	businessUserId: string
): Promise<ValidateUserExist> {
	const path = constants.Paths().checkUserExists(businessUserId);
	return await api.Get<ValidateUserExist>(path);
}

export async function CreatePlantUser(
	api: ApiPromiseClient,
	request: CreatePlantUserRequest
): Promise<CreatePlantUserResponse> {
	const path = constants.Paths().createPlantUser();
	const resp = await api.Post<CreatePlantUserResponse>(path, request);
	
	if (
		resp?.errorList &&
		Array.isArray(resp.errorList) &&
		resp.errorList.length
	) {
		const err: any = new Error(
			resp.errorList[0]?.message || "Create plant user failed"
		);
		err.response = { data: resp };
		throw err;
	}
	
	return resp;
}

export async function GetPlantUser(
	api: ApiPromiseClient,
	businessUserId: string
): Promise<PlantUserResponse> {
	const path = constants.Paths().getPlantUser(businessUserId);
	const resp = await api.Get<PlantUserResponse>(path);
	return resp;
}

export async function UpdatePlantUser(
	api: ApiPromiseClient,
	businessUserId: string,
	request: UpdatePlantUserRequest
): Promise<CreatePlantUserResponse> {
	const path = constants.Paths().updatePlantUser(businessUserId);
	const resp = await api.Put<CreatePlantUserResponse>(path, request);
	
	if (
		resp?.errorList &&
		Array.isArray(resp.errorList) &&
		resp.errorList.length
	) {
		const err: any = new Error(
			resp.errorList[0]?.message || "Update plant user failed"
		);
		err.response = { data: resp };
		throw err;
	}
	
	return resp;
}

export async function DeleteSectorAdmin(
	api: ApiPromiseClient,
	businessUserId: string,
	request: DeleteSectorAdminRequest
): Promise<void> {
	const path = constants.Paths().deleteSectorAdmin(businessUserId);
	await api.Delete<any>(path, {
		data: request,
	});
}

export async function AddSectorAdmin(
	api: ApiPromiseClient,
	request: AddSectorAdminRequest
): Promise<void> {
	const path = constants.Paths().addSectorAdmin();
	const resp = await api.Post<any>(path, request);
	
	if (
		resp?.errorList &&
		Array.isArray(resp.errorList) &&
		resp.errorList.length
	) {
		const err: any = new Error(
			resp.errorList[0]?.message || "Add sector admin failed"
		);
		err.response = { data: resp };
		throw err;
	}
}

export async function DeletePlantUser(
	api: ApiPromiseClient,
	businessUserId: string,
	request: DeletePlantUserRequest
): Promise<void> {
	const path = constants.Paths().deletePlantUser(businessUserId);
	await api.Delete<void>(path, {
		data: request,
	});
}